/**
 * ActionIsland - Left-edge floating toolbar for ramp actions
 *
 * The "verb" side — each icon triggers an action or opens a slide-out panel.
 * Replaces the previous RampToolbar with island styling.
 */

import { Plus, Plane, PlaneLanding, Building2, Filter, Ruler, Calculator } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Island } from './Island'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useRampPanelStore, type RampPanel } from '@/stores/rampPanelStore'
import { useRampFilterStore } from '@/stores/rampFilterStore'

interface ActionIslandProps {
  onAddAircraft: () => void
  onToggleFilters: () => void
  onOpenPriceCalc: () => void
  minimized?: boolean
  arrivalsCount?: number
  /** Demo: FlightBridge sync button */
  demoSlot?: React.ReactNode
}

interface ActionButton {
  id: string
  icon: React.ElementType
  label: string
  shortcut?: string
  onClick: () => void
  primary?: boolean
  panel?: RampPanel
  hasBadge?: boolean
}

export function ActionIsland({ onAddAircraft, onToggleFilters, onOpenPriceCalc, minimized, arrivalsCount = 0, demoSlot }: ActionIslandProps) {
  const { activePanel, togglePanel } = useRampPanelStore()
  const hasActiveFilters = useRampFilterStore((s) => s.hasActiveFilters())

  const actions: ActionButton[] = [
    {
      id: 'add',
      icon: Plus,
      label: 'Add Aircraft',
      shortcut: 'N',
      onClick: onAddAircraft,
      primary: true,
    },
  ]

  const tools: ActionButton[] = [
    {
      id: 'aircraft-list',
      icon: Plane,
      label: 'Aircraft List',
      shortcut: 'L',
      onClick: () => togglePanel('aircraft-list'),
      panel: 'aircraft-list',
    },
    {
      id: 'arrivals',
      icon: PlaneLanding,
      label: 'Arrivals & Returns',
      shortcut: 'A',
      onClick: () => togglePanel('arrivals'),
      panel: 'arrivals',
      hasBadge: arrivalsCount > 0,
    },
    {
      id: 'spaces-list',
      icon: Building2,
      label: 'Spaces List',
      shortcut: 'H',
      onClick: () => togglePanel('spaces-list'),
      panel: 'spaces-list',
    },
    {
      id: 'quick-fit',
      icon: Ruler,
      label: 'Quick Fit',
      shortcut: 'Q',
      onClick: () => togglePanel('quick-fit'),
      panel: 'quick-fit',
    },
    {
      id: 'price-calc',
      icon: Calculator,
      label: 'Price Calculator',
      shortcut: 'P',
      onClick: onOpenPriceCalc,
    },
  ]

  const filterAction: ActionButton = {
    id: 'filters',
    icon: Filter,
    label: 'Filters',
    shortcut: 'F',
    onClick: onToggleFilters,
    hasBadge: hasActiveFilters,
  }

  return (
    <Island
      minimized={minimized}
      className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2"
    >
      <TooltipProvider delayDuration={300}>
        <div className="flex flex-col items-center gap-2">
          {/* Primary: Add Aircraft */}
          {actions.map((action) => (
            <Tooltip key={action.id}>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="w-10 h-10 rounded-full bg-primary hover:bg-primary/90 shadow-md"
                  onClick={action.onClick}
                >
                  <action.icon className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="flex items-center gap-2">
                <span>{action.label}</span>
                {action.shortcut && (
                  <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-muted rounded">
                    {action.shortcut}
                  </kbd>
                )}
              </TooltipContent>
            </Tooltip>
          ))}

          {/* Separator */}
          <div className="w-6 h-px bg-border" />

          {/* Tool buttons */}
          {tools.map((tool) => {
            const isActive = tool.panel && activePanel === tool.panel
            return (
              <Tooltip key={tool.id}>
                <TooltipTrigger asChild>
                  <Button
                    variant={isActive ? 'secondary' : 'ghost'}
                    size="icon"
                    className={cn(
                      'w-10 h-10 rounded-xl transition-all relative',
                      isActive && 'bg-accent ring-1 ring-accent-foreground/10'
                    )}
                    onClick={tool.onClick}
                  >
                    <tool.icon className="w-5 h-5" />
                    {tool.hasBadge && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-blue-500" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="flex items-center gap-2">
                  <span>{tool.label}</span>
                  {tool.shortcut && (
                    <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-muted rounded">
                      {tool.shortcut}
                    </kbd>
                  )}
                </TooltipContent>
              </Tooltip>
            )
          })}

          {/* Demo slot — FlightBridge sync + reset (KOPF only) */}
          {demoSlot && (
            <>
              <div className="w-6 h-px bg-border" />
              {demoSlot}
              <div className="w-6 h-px bg-border" />
            </>
          )}
          {!demoSlot && <div className="w-6 h-px bg-border" />}

          {/* Filter button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'w-10 h-10 rounded-xl transition-all relative',
                  filterAction.hasBadge && 'text-primary'
                )}
                onClick={filterAction.onClick}
              >
                <filterAction.icon className="w-5 h-5" />
                {filterAction.hasBadge && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" className="flex items-center gap-2">
              <span>{filterAction.label}</span>
              {filterAction.shortcut && (
                <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-muted rounded">
                  {filterAction.shortcut}
                </kbd>
              )}
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </Island>
  )
}
